import { useState, useMemo, useCallback, useEffect } from 'react';
import { ApiError, toastError } from '../utils/api';
import { r2Url } from '../utils/r2Url';
import { useToast } from './useToast';
import type { FormatPreset, UpdateProjectRequest } from '@historyjki/shared';
import { FORMAT_PRESETS } from '@historyjki/shared';
import {
  collectPlacementWarnings,
  collectSharpnessWarnings,
  type PreflightWarning,
} from '../utils/printPreflight';
import type { useProject } from './useProject';

type ProjectHookReturn = ReturnType<typeof useProject>;
type Stage = NonNullable<UpdateProjectRequest['stage']>;

export interface ExportOptions {
  includePageNumbers: boolean;
  includeBackCover: boolean;
  blankPageAfterCover?: boolean;
  includeDedication: boolean;
}

/**
 * Business logic for the Export stage: building the print-ready PDF (lazy-loads
 * the pdf builder, uploads the bytes) and triggering the browser download. The
 * component owns only the finishing-touch toggles and their preview.
 */
export function useExportStage(projectHook: ProjectHookReturn) {
  const { project, updateProject, refetch } = projectHook;
  const { showToast } = useToast();

  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(
    project?.pdf_r2_key ? r2Url(project.pdf_r2_key) : null,
  );
  // Warning-only build notes from the last PDF generation (missing image,
  // font fallback, …). Informational — never blocks the export.
  const [buildWarnings, setBuildWarnings] = useState<string[]>([]);

  const spreads = project?.spreads || [];
  const format = (project?.format_preset || 'square-8x8') as FormatPreset;
  const formatPreset = useMemo(
    () => FORMAT_PRESETS.find((f) => f.value === format),
    [format],
  );

  // Live placement preflight (text in trim margins/gutter, image edges in
  // the dead zone) over the saved spreads. Warning-only.
  const geometryWarnings = useMemo(
    () => collectPlacementWarnings(project?.spreads ?? [], format),
    [project?.spreads, format],
  );

  // Print-sharpness preflight is async (it decodes image dimensions once,
  // through the shared cache) — it lands a beat later and merges into the
  // same Print check list.
  const [sharpnessWarnings, setSharpnessWarnings] = useState<PreflightWarning[]>([]);
  useEffect(() => {
    let cancelled = false;
    void collectSharpnessWarnings(project?.spreads ?? [], format).then((ws) => {
      if (!cancelled) setSharpnessWarnings(ws);
    });
    return () => {
      cancelled = true;
    };
  }, [project?.spreads, format]);

  const placementWarnings = useMemo(
    () => [...geometryWarnings, ...sharpnessWarnings],
    [geometryWarnings, sharpnessWarnings],
  );

  const generatePdf = useCallback(
    async (opts: ExportOptions) => {
      if (!project) return;
      setGenerating(true);
      setPdfUrl(null);
      try {
        const { buildBookPdf } = await import('../utils/exportPdf');
        const { getPageSizePt } = await import('../utils/spreadGeometry');

        // Re-read the project so the PDF reflects the spreads exactly as
        // last saved — the in-memory copy can predate recent canvas edits.
        const fresh = (await refetch()) ?? project;

        const { widthPt, heightPt } = getPageSizePt(fresh.format_preset);
        const { bytes, warnings } = await buildBookPdf(fresh, fresh.spreads ?? [], {
          pageWidth: widthPt,
          pageHeight: heightPt,
          includePageNumbers: opts.includePageNumbers,
          includeBackCover: opts.includeBackCover,
          blankPageAfterCover: opts.blankPageAfterCover,
          dedicationPage: opts.includeDedication,
        });
        setBuildWarnings(warnings);

        const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
        const uploadRes = await fetch(`/api/projects/${project.id}/export-pdf`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/pdf' },
          body: blob,
        });
        if (!uploadRes.ok) {
          const errBody = await uploadRes
            .json()
            .catch(() => ({ message: 'Failed to upload PDF' }));
          throw new ApiError(uploadRes.status, errBody.message || 'Failed to upload PDF');
        }
        const data = (await uploadRes.json()) as { pdf_url: string };
        setPdfUrl(data.pdf_url);
        // Pull the fresh pdf_generated_at so the "outdated PDF" hint clears.
        await refetch();
        showToast(
          warnings.length > 0
            ? `PDF generated — ${warnings.length} print ${warnings.length === 1 ? 'note' : 'notes'} in the print check.`
            : 'PDF generated successfully!',
          'success',
        );
      } catch (err) {
        toastError(showToast, err, 'Failed to generate PDF');
      } finally {
        setGenerating(false);
      }
    },
    [project, refetch, showToast],
  );

  const downloadPdf = useCallback(() => {
    if (!pdfUrl || !project) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${project.title || 'HistoRyjki-Book'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [pdfUrl, project]);

  const goToStage = useCallback(
    (stage: Stage) => {
      void updateProject({ stage });
    },
    [updateProject],
  );

  return {
    project,
    spreads,
    format,
    formatPreset,
    generating,
    pdfUrl,
    placementWarnings,
    buildWarnings,
    generatePdf,
    downloadPdf,
    goToStage,
  };
}
